import { Api, Model, ModelRow, SettingsLoader } from "@mcleod/core";
import {
    Component, DataSource, DataSourceRemoteDataChangeEvent, PanelProps, SelectionEvent, Snackbar, Tab
} from "@mcleod/components";
import { Router, UserLayoutProvider } from "@mcleod/common";
import { LMEUserSettings } from "@mcleod/general";
import { MultiProfilePlanningTabs } from "@mcleod/dispatch/src/planning/MultiProfilePlanningTabs";
import { AutogenLayoutDriverSettlementMultiProfile } from "./autogen/AutogenLayoutDriverSettlementMultiProfile";
import { DriverSettlement } from "./DriverSettlement";

export class DriverSettlementMultiProfile extends AutogenLayoutDriverSettlementMultiProfile {
    private userProfiles: string[] = [];
    private payrollControl: ModelRow;

    async onLoad(): Promise<void> {
        if (LMEUserSettings.get()?.driver_settlement_profile != null)
            this.userProfiles.push(LMEUserSettings.get().driver_settlement_profile);
        if (LMEUserSettings.get()?.driver_settlement_profile2 != null)
            this.userProfiles.push(LMEUserSettings.get().driver_settlement_profile2);
        if (LMEUserSettings.get()?.driver_settlement_profile3 != null)
            this.userProfiles.push(LMEUserSettings.get().driver_settlement_profile3);
        if (LMEUserSettings.get()?.driver_settlement_profile4 != null)
            this.userProfiles.push(LMEUserSettings.get().driver_settlement_profile4);

        new MultiProfilePlanningTabs({
            tabset: this.tabsetDriverSettlementProfiles,
            profileDataSource: this.sourceDriverSettlementProfile,
            mainLayoutName: "lme/settlement/driver-settlement/DriverSettlement",
            advancedFilterLayout: "lme/settlement/driver-settlement/DriverSettlementProfileFilter",
            startingProfiles: this.userProfiles,
            profileDescrFieldName: "descr",
            userSettingsCallback: () => UserLayoutProvider.navigateTo(null, null, { scrollTo: "tabBackOffice" }),
            dataSourceToAddListener: this.sourceOrderPayablesCache
        });
        this.sourcePayeeCache.search({ is_dummy: true }).then(() => this.sourcePayeeCache.clear());
        this.sourceOrderPayablesCache.search({ is_dummy: true }).then(() => this.sourceOrderPayablesCache.clear());
        this.sourceStateFederalTaxDriverSettlement.search().then(() => this.sourceStateFederalTaxDriverSettlement.clear());
        this.sourceVoidedPayablesDriverSettlement.search().then(() => this.sourceVoidedPayablesDriverSettlement.clear());
    }

    handleRemoteDataChange(event: DataSourceRemoteDataChangeEvent) {
        event.preventDefault();
        this.tabsetDriverSettlementProfiles.tabs.forEach((tab: Tab) => {
            tab.components.forEach((component: Component) => {
                if (component instanceof DriverSettlement) {
                    if ("lme/settlement/order-payables-cache" === (event.target as DataSource).url) {
                        const dataSource = component.sourceOrderPayablesCache;
                        dataSource.handleDataChange(event.change);
                    } else if ("lme/settlement/payee-cache" === (event.target as DataSource).url) {
                        const dataSource = component.sourcePayeeCache;
                        dataSource.handleDataChange(event.change);
                    } else if ("lme/settlement/driver-settlement/state-federal-tax-driver-settlement" === (event.target as DataSource).url) {
                        const dataSource = component.sourceStateFederalTaxDriverSettlement;
                        dataSource.handleDataChange(event.change);
                    } else if ("lme/settlement/voided-payables-driver-settlement" === (event.target as DataSource).url) {
                        const dataSource = component.sourceVoidedPayablesDriverSettlement;
                        dataSource.handleDataChange(event.change);
                    }
                }
            });
        });
    }

    // Override the main router's padding to get the vertical profile tabset to be flush with the edge of the screen
    getRouterProps(): Partial<PanelProps> {
        return { padding: 0 };
    }

    tabsetDriverSettlementProfilesAfterTabSelection(event: SelectionEvent) {
        if (event.oldSelection) {
            const oldTab = event.oldSelection as Tab;
            const driverSettlementLayout = oldTab.components?.find((comp: Component) => comp instanceof DriverSettlement) as DriverSettlement;
            if (driverSettlementLayout != null && driverSettlementLayout.profileRow != null) {
                Router.instance.dismissAllPopups();
            }
        }
        if (event.newSelection) {
            const tab = event.newSelection as Tab;
            const driverSettlementLayout = tab.components?.find((comp: Component) => comp instanceof DriverSettlement) as DriverSettlement;
            if (driverSettlementLayout != null && driverSettlementLayout.profileRow != null) {
                if (driverSettlementLayout.snackbars.size > 0) {
                    driverSettlementLayout.snackbars.forEach((snackbar) => {
                        Snackbar.showSnackbar(snackbar, { persist: true });
                    });
                } else {
                    driverSettlementLayout.checkBonusTaxRatesSetup();
                }
                driverSettlementLayout.setCutoffDates();
            }
            if (driverSettlementLayout != null) {
                driverSettlementLayout.updateCutoffDate = this.updateCutoffDate.bind(this);
            }
        }
    }

    async updateCutoffDate(profile: ModelRow): Promise<ModelRow | null> {
        const payeeType = profile.isNull("payee_type") ? "B" : profile.get("payee_type");
        const response = await Api.post("lme/settlement/update-cutoff-date", {
            payee_type: payeeType
        });
        if (response?.data?.[0]?.cutoff_date) {
            this.payrollControl = await Model.searchSingleRecord("lme/dispatch/payroll-control");
            this.tabsetDriverSettlementProfiles.components.forEach((tab: Tab) => {
                tab.components.forEach((component: Component) => {
                    if (component instanceof DriverSettlement) {
                        component.payrollControl = this.payrollControl;
                    }
                });
            });
            // reload auth settings to get the new cutoff date
            SettingsLoader.loadAuthSettings(null, null, null);
            return this.payrollControl;
        }
        return null;
    }

}
